import type { CrudArgs } from "./composables/ContextVoter";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

export type NotificationSettingVoteItem = Omit<CrudArgs, "@id"> & {
  subscriber?: string | null
  permissionTemplateGroup?: string | null
  ptgClient?: string | null
  ptgLocation?: string | null
};

export class NotificationSettingVoter extends ContextVoter implements ContextVoterInterface<NotificationSettingVoteItem> {
  /**
   * Mirrors the backend NotificationSettingVoter::hasContextScopedAccess:
   * access via the setting's client or location, falling back to the
   * permission template group's client or location.
   */
  private hasContextScopedAccess(
    item: NotificationSettingVoteItem,
    contextAttribute: PermissionAttributesEnum,
  ): boolean {
    if ((item.client || item.location) && this.hasAccess({
      client: item.client,
      location: item.location,
      attribute: contextAttribute,
    })) {
      return true;
    }

    if (!item.permissionTemplateGroup) {
      return false;
    }

    if (!item.ptgClient && !item.ptgLocation) {
      return false;
    }

    return this.hasAccess({
      client: item.ptgClient,
      location: item.ptgLocation,
      attribute: contextAttribute,
    });
  }

  private isOwnSubscriberSetting(item?: NotificationSettingVoteItem): boolean {
    if (!item?.subscriber || !this.permissionService.userIri) {
      return false;
    }

    return item.subscriber === this.permissionService.userIri;
  }

  private canReadAny(): boolean {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationNotificationsettingReadany,
    });
  }

  /**
   * Mirrors the API NotificationSettingVoter::canReadViaClient (`GET /clients/{client}/notification_settings` and
   * the notification defaults impact of an organisation): the global read right, or the organisation is readable
   * and `notification_setting.read` is held on it.
   */
  public canReadCollectionViaClient = (item: NotificationSettingVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }

    if (!item.client || !this.canReadClientContext(item.client)) {
      return false;
    }

    return this.hasAccess({ client: item.client, attribute: PermissionAttributesEnum.ContextOperationNotificationsettingRead });
  };

  /**
   * Mirrors the API NotificationSettingVoter::canReadViaLocation (`GET /locations/{location}/notification_settings`
   * and the notification defaults impact of a location): the global read right, or the location is readable and
   * `notification_setting.read` is held on its organisation or on the location itself.
   */
  public canReadCollectionViaLocation = (item: NotificationSettingVoteItem = {}) => {
    if (this.canReadAny()) {
      return true;
    }

    if (!item.location || !this.canReadLocationContext(item.client, item.location)) {
      return false;
    }

    return this.hasAccess({
      client: item.client,
      location: item.location,
      attribute: PermissionAttributesEnum.ContextOperationNotificationsettingRead,
    });
  };

  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    const voteItem = item as NotificationSettingVoteItem | undefined;

    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationNotificationsettingCreateany,
    })) {
      return true;
    }

    if (voteItem?.subscriber) {
      return this.isOwnSubscriberSetting(voteItem);
    }

    return this.hasContextScopedAccess(
      voteItem ?? {},
      PermissionAttributesEnum.ContextOperationNotificationsettingCreate,
    );
  };

  public canRead: ContextVoterInterface["canRead"] = (item) => {
    const voteItem = item as NotificationSettingVoteItem;

    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationNotificationsettingReadany,
    })) {
      return true;
    }

    if (voteItem.subscriber) {
      return this.isOwnSubscriberSetting(voteItem);
    }

    return this.hasContextScopedAccess(
      voteItem,
      PermissionAttributesEnum.ContextOperationNotificationsettingRead,
    );
  };

  public canDelete: ContextVoterInterface["canDelete"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    const voteItem = item as NotificationSettingVoteItem;

    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationNotificationsettingDeleteany,
    })) {
      return true;
    }

    if (voteItem.subscriber) {
      return this.isOwnSubscriberSetting(voteItem);
    }

    return this.hasContextScopedAccess(
      voteItem,
      PermissionAttributesEnum.ContextOperationNotificationsettingDelete,
    );
  };

  public canUpdate: ContextVoterInterface["canUpdate"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    const voteItem = item as NotificationSettingVoteItem;

    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationNotificationsettingUpdateany,
    })) {
      return true;
    }

    if (voteItem.subscriber) {
      return this.isOwnSubscriberSetting(voteItem);
    }

    return this.hasContextScopedAccess(
      voteItem,
      PermissionAttributesEnum.ContextOperationNotificationsettingUpdate,
    );
  };

  public canManage: AggregateVoterCheck<NotificationSettingVoteItem> = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this.canCreate(item)
      || this.canUpdate(item)
      || this.canDelete(item);
  };
}
